import React, { useEffect, useState } from "react";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import { FrontendCompany, FrontendUserInfo } from "node-library/dist/portal";
import {
	FrontendContentPage,
	Severity,
	UserType,
} from "node-library/dist/constants";
import DesktopSidebar from "./desktopSidebar";
import NewOrEditOrder from "../mainPages/newOrEditOrderPage";
import UserProfilePage from "../userProfile/userProfilePage";
import ViewOrdersPage from "../mainPages/viewOrders/client_view/viewOrdersPage";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import UnsavedChangesModal from "../../common-components/modals/unsavedChangesModal";
import RosterPage from "../mainPages/roster/rosterPage";
import OrganizationPage from "../mainPages/organization/organizationPage";
import { checkUserTypeMeetsRequirement } from "../../helpers/userHelper";
import AdminViewOrdersPage from "../mainPages/viewOrders/admin_view/adminViewOrdersPage";
import { useNavigate, useParams } from "react-router-dom";
import HelpAndContactPage from "../mainPages/helpAndContact/helpAndContactPage";
import DesktopHeader from "./desktopHeader";
import PermissionProvider from "../../permissionProvider/PermissionProvider";
import EngineerViewOrdersPage from "../mainPages/viewOrders/engineer_view/engineerViewOrdersPage";

interface Props {}

export default function LandingPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [user, setUser] = useState<FrontendUserInfo>();
	const [company, setCompany] = useState<FrontendCompany>();
	const [contentPage, setContentPage] = useState<FrontendContentPage>(
		FrontendContentPage.VIEW_ORDER_PAGE
	);

	const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(
		window.innerWidth >= 600
	);

	//unsaved changes
	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
		useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	const navigate = useNavigate(); // Hook to navigate programmatically
	const { page } = useParams(); // Get the "page" param from the URL

	function navigateToContentPage(page: FrontendContentPage) {
		switch (page) {
			case FrontendContentPage.NEW_ORDER_PAGE:
				navigate("/portal/new-order");
				break;
			case FrontendContentPage.VIEW_ORDER_PAGE:
				navigate("/portal/order-history");
				break;
			case FrontendContentPage.USER_INFO_PAGE:
				navigate("/portal/profile");
				break;
			case FrontendContentPage.ROSTER_PAGE:
				navigate("/portal/roster");
				break;
			case FrontendContentPage.ORGANIZATION_PAGE:
				navigate("/portal/organization");
				break;
			case FrontendContentPage.HELP_PAGE:
				navigate("/portal/contact");
				break;
			default:
				navigate("/portal/order-history");
				break;
		}
		setContentPage(page);
	}

	// function navigateToContentPageWithUnsavedCheck(page: FrontendContentPage) {
	// 	if (contentPage === page) {
	// 		return;
	// 	}
	// 	if (unsavedChanges) {
	// 		navigateToContentPage(page);
	// 	} else {
	// 		setProceedAction(() => () => {
	// 			navigateToContentPage(page);
	// 		});
	// 		setUnsavedChangesModalOpen(true);
	// 	}
	// }

	useEffect(() => {
		// Get the user info from cookies
		const userInfo = localStorage.getItem("billionspectra_userInfo");
		const companyInfo = localStorage.getItem("billionspectra_companyInfo");
		if (userInfo) {
			setUser(JSON.parse(userInfo));
		}
		if (companyInfo) {
			setCompany(JSON.parse(companyInfo));
		}
	}, []);

	useEffect(() => {
		switch (page) {
			case "new-order":
				setContentPage(FrontendContentPage.NEW_ORDER_PAGE);
				break;
			case "order-history":
				setContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
				break;
			case "profile":
				setContentPage(FrontendContentPage.USER_INFO_PAGE);
				break;
			case "roster":
				setContentPage(FrontendContentPage.ROSTER_PAGE);
				break;
			case "organization":
				setContentPage(FrontendContentPage.ORGANIZATION_PAGE);
				break;
			case "contact":
				setContentPage(FrontendContentPage.HELP_PAGE);
				break;
			default:
				setContentPage(FrontendContentPage.VIEW_ORDER_PAGE); // Default page
				break;
		}
	}, [page]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (unsavedChanges) {
				const confirmationMessage = translation("UnsavedChangesMessage");
				// Standard for most browsers
				event.returnValue = confirmationMessage;
				return confirmationMessage;
			}
		};

		// Attach the event listener
		window.addEventListener("beforeunload", handleBeforeUnload);

		// Cleanup the event listener on component unmount
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [unsavedChanges]);

	// Listen for the back button or history changes
	useEffect(() => {
		const handlePopState = (event: PopStateEvent) => {
			setUnsavedChanges(false);
		};

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);

	let contentPageElement: React.ReactNode;
	switch (contentPage) {
		case FrontendContentPage.NEW_ORDER_PAGE:
			contentPageElement = (
				<NewOrEditOrder
					user={user}
					company={company}
					gotoViewOrderPage={() => {
						navigateToContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
					}}
					setUnsavedChanges={setUnsavedChanges}
				></NewOrEditOrder>
			);
			break;
		case FrontendContentPage.VIEW_ORDER_PAGE:
			if (
				checkUserTypeMeetsRequirement(user?.role as UserType, UserType.ADMIN)
			) {
				contentPageElement = (
					<PermissionProvider role={user?.role as UserType}>
						<AdminViewOrdersPage
							user={user}
							company={company}
							setUnsavedChanges={setUnsavedChanges}
						></AdminViewOrdersPage>
					</PermissionProvider>
				);
			} else if (
				checkUserTypeMeetsRequirement(user?.role as UserType, UserType.ENGINEER)
			) {
				contentPageElement = (
					<PermissionProvider role={user?.role as UserType}>
						<EngineerViewOrdersPage
							user={user}
							company={company}
							setUnsavedChanges={setUnsavedChanges}
						></EngineerViewOrdersPage>
					</PermissionProvider>
				);
			} else {
				contentPageElement = (
					<ViewOrdersPage
						user={user}
						company={company}
						navigateToContentPage={navigateToContentPage}
						setUnsavedChanges={setUnsavedChanges}
					></ViewOrdersPage>
				);
			}
			break;
		case FrontendContentPage.USER_INFO_PAGE:
			contentPageElement = (
				<UserProfilePage
					user={user}
					setUser={setUser}
					setUnsavedChanges={setUnsavedChanges}
				></UserProfilePage>
			);

			break;
		case FrontendContentPage.ROSTER_PAGE:
			contentPageElement = (
				<RosterPage
					setUnsavedChanges={setUnsavedChanges}
					frontendUser={user}
					setUser={setUser}
				></RosterPage>
			);
			break;
		case FrontendContentPage.ORGANIZATION_PAGE:
			contentPageElement = (
				<OrganizationPage
					setUnsavedChanges={setUnsavedChanges}
					frontendUser={user}
				></OrganizationPage>
			);
			break;
		case FrontendContentPage.HELP_PAGE:
			contentPageElement = (
				<HelpAndContactPage company={company}></HelpAndContactPage>
			);
			break;
	}

	return !user ? (
		<h2>Loading...</h2>
	) : (
		<div>
			<div className="landing-page-container">
				<DesktopSidebar
					user={user}
					contentPage={contentPage}
					unsavedChanges={unsavedChanges}
					navigateToContentPage={navigateToContentPage}
					isExpanded={isSidebarExpanded}
					setIsExpanded={setIsSidebarExpanded}
					setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
					setProceedAction={setProceedAction}
				></DesktopSidebar>
				<DesktopHeader
					user={user}
					contentPage={contentPage}
					unsavedChanges={unsavedChanges}
					navigateToContentPage={navigateToContentPage}
					setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
					isSidebarExtended={isSidebarExpanded}
					setProceedAction={setProceedAction}
					isExpanded={isSidebarExpanded}
				></DesktopHeader>
				<div
					className={`landing-page-sidebar-and-content-container ${
						isSidebarExpanded ? `` : `content-container-sidebar-collapsed`
					}`}
				>
					<div className={`landing-page-content-container `}>
						{contentPageElement}
					</div>
				</div>
			</div>
			<UnsavedChangesModal
				open={unsavedChangesModalOpen}
				setOpen={setUnsavedChangesModalOpen}
				setUnsavedChanges={setUnsavedChanges}
				proceedAction={proceedAction}
			></UnsavedChangesModal>
		</div>
	);
}
